import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Handshake, Mail, MessageCircle, Check, X, RotateCcw,
  Clock, AlertTriangle, RefreshCw, Search, ExternalLink, Plus,
} from 'lucide-react';
import { T, ROLES, springs } from '../lib/tokens';
import { supabase } from '../lib/supabase';
import { isLocalAvailable, apiFetch } from '../lib/api';
import { Glass } from '../components/glass/Glass';

const STALE_DAYS = 7;

const fmtRelative = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.floor(ms / 86400000);
  if (days < 0) {
    const ahead = -days;
    if (ahead === 0) return 'today';
    if (ahead === 1) return 'tomorrow';
    if (ahead < 7) return `in ${ahead}d`;
    if (ahead < 30) return `in ${Math.round(ahead/7)}w`;
    return `in ${Math.round(ahead/30)}mo`;
  }
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.round(days/7)}w ago`;
  return `${Math.round(days/30)}mo ago`;
};

const isStale = (p) => {
  if (p.status !== 'open') return false;
  if (p.due_at) return new Date(p.due_at) < new Date();
  const ageDays = (Date.now() - new Date(p.committed_at).getTime()) / 86400000;
  return ageDays > STALE_DAYS;
};

const isOverdue = (p) => {
  if (p.status !== 'open') return false;
  return p.due_at && new Date(p.due_at) < new Date();
};

const PromiseRow = ({ promise, onClose, onDrop, onReopen }) => {
  const [busy, setBusy] = useState(false);
  const role = promise.roles;
  const venture = role ? ROLES[role.slug] : null;
  const overdue = isOverdue(promise);
  const stale = isStale(promise);

  const handle = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  const SourceIcon = promise.source_type === 'meeting' ? MessageCircle
    : promise.source_type === 'email' ? Mail
    : Handshake;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={springs.snappy}
      style={{
        display: 'grid',
        gridTemplateColumns: '24px 1fr auto',
        gap: 14,
        alignItems: 'flex-start',
        padding: '14px 16px',
        borderBottom: '1px solid rgba(0,0,0,0.05)',
        opacity: promise.status === 'open' ? 1 : 0.55,
      }}
    >
      <div style={{ paddingTop: 2 }}>
        <SourceIcon size={14} color={T.textMuted} />
      </div>

      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
          {promise.direction === 'outgoing' ? (
            <span style={{
              fontSize: 9, fontWeight: 700, color: T.amber, background: T.amberLight,
              padding: '2px 6px', borderRadius: 5, letterSpacing: '0.04em',
            }}>YOU OWE</span>
          ) : (
            <span style={{
              fontSize: 9, fontWeight: 700, color: T.blue, background: T.blueLight,
              padding: '2px 6px', borderRadius: 5, letterSpacing: '0.04em',
            }}>OWED TO YOU</span>
          )}
          {venture && (
            <span style={{
              fontSize: 9, fontWeight: 600, color: venture.color, background: venture.light,
              padding: '2px 6px', borderRadius: 5,
            }}>{venture.label}</span>
          )}
          {overdue && (
            <span style={{
              fontSize: 9, fontWeight: 700, color: T.red, background: T.redLight,
              padding: '2px 6px', borderRadius: 5, display: 'flex', alignItems: 'center', gap: 3,
            }}>
              <AlertTriangle size={9} /> OVERDUE
            </span>
          )}
          {!overdue && stale && (
            <span style={{
              fontSize: 9, fontWeight: 700, color: T.amber, background: T.amberLight,
              padding: '2px 6px', borderRadius: 5,
            }}>STALE</span>
          )}
        </div>
        <div style={{ fontSize: 13, color: T.text, lineHeight: 1.4, marginBottom: 4 }}>
          {promise.description}
        </div>
        <div style={{ fontSize: 11, color: T.textMuted, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {promise.counterparty_name && (
            <span><strong>{promise.counterparty_name}</strong>{promise.counterparty_email ? ` · ${promise.counterparty_email}` : ''}</span>
          )}
          {!promise.counterparty_name && promise.counterparty_email && <span>{promise.counterparty_email}</span>}
          <span>committed {fmtRelative(promise.committed_at)}</span>
          {promise.due_at && <span style={{ color: overdue ? T.red : T.textMid }}>due {fmtRelative(promise.due_at)}</span>}
          {promise.source_url && (
            <a href={promise.source_url} target="_blank" rel="noopener noreferrer" style={{
              color: T.blue, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 3,
            }}>
              source <ExternalLink size={10} />
            </a>
          )}
        </div>
        {promise.source_excerpt && promise.source_excerpt !== promise.description && (
          <div style={{
            fontSize: 11, color: T.textMuted, marginTop: 6,
            padding: '6px 10px', borderLeft: '2px solid rgba(0,0,0,0.08)',
            background: 'rgba(0,0,0,0.02)', borderRadius: 4, fontStyle: 'italic',
          }}>
            "{promise.source_excerpt.slice(0, 220)}{promise.source_excerpt.length > 220 ? '…' : ''}"
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 4 }}>
        {promise.status === 'open' && (
          <>
            <button onClick={() => handle(() => onClose(promise.id))} disabled={busy} title="Mark done" style={{
              background: T.greenLight, border: 'none', borderRadius: 8, cursor: 'pointer',
              padding: 6, color: T.green, opacity: busy ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Check size={14} />
            </button>
            <button onClick={() => handle(() => onDrop(promise.id))} disabled={busy} title="Drop" style={{
              background: 'rgba(0,0,0,0.04)', border: 'none', borderRadius: 8, cursor: 'pointer',
              padding: 6, color: T.textMuted, opacity: busy ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <X size={14} />
            </button>
          </>
        )}
        {promise.status !== 'open' && (
          <button onClick={() => handle(() => onReopen(promise.id))} disabled={busy} title="Reopen" style={{
            background: 'rgba(0,0,0,0.04)', border: 'none', borderRadius: 8, cursor: 'pointer',
            padding: 6, color: T.textMid, opacity: busy ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <RotateCcw size={14} />
          </button>
        )}
      </div>
    </motion.div>
  );
};

const ManualAddDialog = ({ roles, onClose, onAdded }) => {
  const [direction, setDirection] = useState('outgoing');
  const [description, setDescription] = useState('');
  const [counterparty, setCounterparty] = useState('');
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!description.trim()) return;
    setSaving(true);
    await supabase.from('promises').insert({
      direction,
      description: description.trim(),
      counterparty_name: counterparty.trim() || null,
      counterparty_email: email.trim().toLowerCase() || null,
      role_id: roleId || null,
      source_type: 'manual',
      committed_at: new Date().toISOString(),
      due_at: dueDate ? new Date(dueDate + 'T17:00:00Z').toISOString() : null,
    });
    setSaving(false);
    onAdded();
    onClose();
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 10, fontSize: 13,
    border: '1px solid rgba(0,0,0,0.10)', background: 'rgba(255,255,255,0.85)',
    fontFamily: 'inherit', color: T.text, outline: 'none',
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)', backdropFilter: 'blur(4px)', zIndex: 100 }} />
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }}
        transition={springs.gentle}
        style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 460, zIndex: 101 }}
      >
        <Glass depth={3} style={{ padding: 28 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Add Promise</h3>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
              <X size={16} color={T.textMuted} />
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 6 }}>
              {['outgoing', 'incoming'].map(d => (
                <button key={d} onClick={() => setDirection(d)} style={{
                  flex: 1, padding: '8px 12px', borderRadius: 8,
                  border: direction === d ? `1px solid ${T.amber}` : '1px solid rgba(0,0,0,0.08)',
                  background: direction === d ? T.amberLight : 'rgba(255,255,255,0.6)',
                  color: direction === d ? T.amber : T.textMid,
                  fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                }}>
                  {d === 'outgoing' ? 'You owe' : 'Owed to you'}
                </button>
              ))}
            </div>
            <textarea style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }}
              placeholder="What was promised? e.g. Send updated pitch deck"
              value={description} onChange={e => setDescription(e.target.value)} autoFocus />
            <input style={inputStyle} placeholder="Counterparty name" value={counterparty} onChange={e => setCounterparty(e.target.value)} />
            <input style={inputStyle} placeholder="Counterparty email (optional)" value={email} onChange={e => setEmail(e.target.value)} />
            <select style={inputStyle} value={roleId} onChange={e => setRoleId(e.target.value)}>
              <option value="">— No venture —</option>
              {roles.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
            <input style={inputStyle} type="date" placeholder="Due date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
            <button onClick={save} disabled={!description.trim() || saving} style={{
              padding: '10px 20px', borderRadius: 10, border: 'none',
              background: T.amber, color: '#fff', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              opacity: !description.trim() || saving ? 0.5 : 1, marginTop: 4,
            }}>{saving ? 'Saving…' : 'Add Promise'}</button>
          </div>
        </Glass>
      </motion.div>
    </>
  );
};

export const PromisesView = ({ promises, roles, onUpdate }) => {
  const [filter, setFilter] = useState('open');
  const [ventureSlug, setVentureSlug] = useState('all');
  const [search, setSearch] = useState('');
  const [scanning, setScanning] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const closePromise = async (id) => {
    if (isLocalAvailable()) {
      await apiFetch(`/promises/${id}/close`, { method: 'POST' });
    } else {
      await supabase.from('promises').update({ status: 'done', closed_at: new Date().toISOString() }).eq('id', id);
    }
    onUpdate();
  };
  const dropPromise = async (id) => {
    if (isLocalAvailable()) {
      await apiFetch(`/promises/${id}/drop`, { method: 'POST' });
    } else {
      await supabase.from('promises').update({ status: 'dropped', closed_at: new Date().toISOString() }).eq('id', id);
    }
    onUpdate();
  };
  const reopenPromise = async (id) => {
    if (isLocalAvailable()) {
      await apiFetch(`/promises/${id}/reopen`, { method: 'POST' });
    } else {
      await supabase.from('promises').update({ status: 'open', closed_at: null }).eq('id', id);
    }
    onUpdate();
  };

  const runScan = async () => {
    if (!isLocalAvailable()) return;
    setScanning(true);
    try {
      await apiFetch(`/promises/scan`, { method: 'POST', body: JSON.stringify({ days: 30 }) });
      onUpdate();
    } catch (err) {
      console.error(err);
    } finally {
      setScanning(false);
    }
  };

  const stats = useMemo(() => {
    const open = promises.filter(p => p.status === 'open');
    return {
      open: open.length,
      stale: open.filter(isStale).length,
      overdue: open.filter(isOverdue).length,
      done: promises.filter(p => p.status === 'done').length,
    };
  }, [promises]);

  const filtered = useMemo(() => {
    let list = promises;
    if (filter === 'open') list = list.filter(p => p.status === 'open');
    else if (filter === 'stale') list = list.filter(p => p.status === 'open' && isStale(p));
    else if (filter === 'overdue') list = list.filter(p => p.status === 'open' && isOverdue(p));
    else if (filter === 'done') list = list.filter(p => p.status === 'done');
    else if (filter === 'dropped') list = list.filter(p => p.status === 'dropped');

    if (ventureSlug !== 'all') {
      if (ventureSlug === 'unassigned') list = list.filter(p => !p.role_id);
      else list = list.filter(p => p.roles?.slug === ventureSlug);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        p.description?.toLowerCase().includes(q)
        || p.counterparty_name?.toLowerCase().includes(q)
        || p.counterparty_email?.toLowerCase().includes(q)
      );
    }

    // Sort: overdue first, then stale, then by due_at, then committed_at desc
    return [...list].sort((a, b) => {
      const ao = isOverdue(a) ? 0 : 1;
      const bo = isOverdue(b) ? 0 : 1;
      if (ao !== bo) return ao - bo;
      if (a.due_at && b.due_at) return new Date(a.due_at) - new Date(b.due_at);
      if (a.due_at) return -1;
      if (b.due_at) return 1;
      return new Date(b.committed_at) - new Date(a.committed_at);
    });
  }, [promises, filter, ventureSlug, search]);

  const FilterChip = ({ id, label, count, color }) => (
    <button onClick={() => setFilter(id)} style={{
      padding: '6px 12px', borderRadius: 999, border: 'none',
      background: filter === id ? (color || T.text) : 'rgba(255,255,255,0.55)',
      color: filter === id ? '#fff' : T.textMid,
      fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
      display: 'flex', alignItems: 'center', gap: 5,
    }}>
      {label}
      {typeof count === 'number' && (
        <span style={{ fontSize: 10, opacity: 0.85 }}>{count}</span>
      )}
    </button>
  );

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={springs.gentle}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: T.text, letterSpacing: '-0.03em' }}>Promises</h1>
          <p style={{ fontSize: 13, color: T.textMuted, marginTop: 4 }}>
            {stats.open} open
            {stats.overdue > 0 && <span style={{ color: T.red, fontWeight: 600 }}> · {stats.overdue} overdue</span>}
            {stats.stale > 0 && <span style={{ color: T.amber, fontWeight: 600 }}> · {stats.stale} stale</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={runScan} disabled={scanning || !isLocalAvailable()} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 10, border: 'none',
            background: T.violetLight, color: T.violet, cursor: 'pointer',
            fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
            opacity: (scanning || !isLocalAvailable()) ? 0.5 : 1,
          }} title={!isLocalAvailable() ? 'Local server required' : 'Scan email + meetings'}>
            <RefreshCw size={12} className={scanning ? 'spin' : ''} /> {scanning ? 'Scanning…' : 'Scan now'}
          </button>
          <button onClick={() => setShowAdd(true)} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 10, border: 'none',
            background: T.amberLight, color: T.amber, cursor: 'pointer',
            fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
          }}>
            <Plus size={12} /> Add Promise
          </button>
        </div>
      </div>

      {/* Filter chips */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        <FilterChip id="open" label="Open" count={stats.open} />
        <FilterChip id="overdue" label="Overdue" count={stats.overdue} color={T.red} />
        <FilterChip id="stale" label="Stale" count={stats.stale} color={T.amber} />
        <FilterChip id="done" label="Done" count={stats.done} color={T.green} />
        <FilterChip id="dropped" label="Dropped" />
        <FilterChip id="all" label="All" />
      </div>

      {/* Venture + search */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <select value={ventureSlug} onChange={e => setVentureSlug(e.target.value)} style={{
          padding: '6px 10px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)',
          background: 'rgba(255,255,255,0.7)', fontSize: 12, fontFamily: 'inherit', color: T.text, outline: 'none',
        }}>
          <option value="all">All ventures</option>
          {roles.map(r => <option key={r.id} value={r.slug}>{r.label}</option>)}
          <option value="unassigned">Unassigned</option>
        </select>
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={12} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: T.textMuted }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search description, name, email…" style={{
            width: '100%', padding: '6px 10px 6px 28px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)',
            background: 'rgba(255,255,255,0.7)', fontSize: 12, fontFamily: 'inherit', color: T.text, outline: 'none',
          }} />
        </div>
      </div>

      <Glass depth={1} style={{ padding: 0, overflow: 'hidden' }} hover={false}>
        {filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: T.textMuted, fontSize: 13 }}>
            {filter === 'open' ? 'No open promises. Click "Scan now" to capture commitments from email and meetings.' : 'Nothing to show.'}
          </div>
        ) : (
          <AnimatePresence>
            {filtered.map(p => (
              <PromiseRow key={p.id} promise={p} onClose={closePromise} onDrop={dropPromise} onReopen={reopenPromise} />
            ))}
          </AnimatePresence>
        )}
      </Glass>

      <AnimatePresence>
        {showAdd && (
          <ManualAddDialog roles={roles} onClose={() => setShowAdd(false)} onAdded={onUpdate} />
        )}
      </AnimatePresence>
    </motion.div>
  );
};
